import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { GiftCardTransaction } from 'api/orders'
import Badge from 'components/Badge'
import { GIFT_CARD_PROVIDER_IMAGES } from 'constants/giftCardProviders'
import { buildTranslate } from 'locales'
import React from 'react'
import { formatCurrency } from 'scenes/RedeemManager/helpers'
import { merchantTheme } from 'theme'

const t = buildTranslate('users.view.activity.orders.detail')

type PropsT = {
  transactions: GiftCardTransaction[]
}

const OrderGiftCardTransactions: React.FC<PropsT> = ({ transactions }) => {
  const { colors } = merchantTheme
  const [css] = useStyletron()

  if (!transactions || transactions.length === 0) {
    return null
  }

  return (
    <>
      <Text.BodyText4 className="mb-xs mt-s" tag="div" bold>
        {t('gift_card_payments')}
      </Text.BodyText4>
      {transactions.map(txn => {
        const providerImage =
          GIFT_CARD_PROVIDER_IMAGES[txn.provider.toLowerCase()]

        return (
          <div
            key={txn.id}
            className={`py-xs px-s mb-xs ${css({
              backgroundColor: colors.grey10,
              borderRadius: '4px',
            })}`}
          >
            <div className="d-flex align-items-center mb-xs">
              <div className="d-flex align-items-center flex-wrap">
                <Text.Header4
                  tag="span"
                  className="mr-xs"
                  color={colors.grey90}
                >
                  {formatCurrency(txn.amount)}
                </Text.Header4>
                <Badge
                  value={t(`gift_card_states.${txn.state}`)}
                  className={css({
                    ...getStateBadgeStyle(txn.state),
                  })}
                />
              </div>
              <Switch
                condition={!!providerImage}
                fallback={
                  <Text.BodyText3
                    className="ml-xs text-capitalize"
                    color={colors.grey70}
                    bold
                  >
                    {txn.provider}
                  </Text.BodyText3>
                }
              >
                <img
                  src={providerImage}
                  alt={txn.provider}
                  height={16}
                  className="ml-xs"
                />
              </Switch>
            </div>
            {txn.references && txn.references.length > 0 && (
              <div
                className={`pt-xs ${css({
                  borderTop: `1px solid ${colors.grey20}`,
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: '8px',
                  alignItems: 'center',
                })}`}
              >
                {txn.references.map(ref => (
                  <React.Fragment key={ref.value}>
                    <div>
                      <Badge
                        value={ref.type}
                        className={css({
                          ...getReferenceTypeBadgeStyle(ref.type),
                        })}
                      />
                    </div>
                    <Text.BodyText4 tag="span" color={colors.grey70}>
                      {ref.value}
                    </Text.BodyText4>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

const getReferenceTypeBadgeStyle = (type: string) => {
  switch (type) {
    case 'authorization':
      return {
        color: merchantTheme.colors.nephritis50,
        backgroundColor: merchantTheme.colors.nephritis20,
      }
    case 'refund':
      return {
        color: merchantTheme.colors.cayenne50,
        backgroundColor: merchantTheme.colors.cayenne10,
      }
    default:
      return {
        color: merchantTheme.colors.grey70,
        backgroundColor: merchantTheme.colors.grey20,
      }
  }
}

const getStateBadgeStyle = (state: string) => {
  switch (state) {
    case 'processed':
      return {
        color: merchantTheme.colors.nephritis50,
        backgroundColor: merchantTheme.colors.nephritis20,
      }
    case 'refunded':
      return {
        color: merchantTheme.colors.cayenne50,
        backgroundColor: merchantTheme.colors.cayenne10,
      }
    default:
      return {
        color: merchantTheme.colors.grey70,
        backgroundColor: merchantTheme.colors.grey20,
      }
  }
}

export default OrderGiftCardTransactions
